import React, { useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const DocumentUpload = ({ onFileUpload, uploadedFile, loading }) => {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      onFileUpload(acceptedFiles[0]);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc']
    },
    multiple: false,
    disabled: loading
  });

  const handleRemoveFile = () => {
    onFileUpload(null);
  };

  const supportedFormats = [
    // { type: 'PDF', icon: <Description />, color: '#f40' },
    { type: 'Word (.docx)', icon: <Description />, color: '#2b579a' },
    // { type: 'Text', icon: <Description />, color: '#333' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUpload color="primary" />
        Upload Functional Requirements Document
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload your FRD document and our AI will generate a comprehensive Technical Requirements Document
      </Typography>

      {/* Supported Formats */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Supported Formats:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {supportedFormats.map((format) => (
            <Chip
              key={format.type}
              icon={format.icon}
              label={format.type}
              variant="outlined"
              sx={{ borderColor: format.color, color: format.color }}
            />
          ))}
        </Box>
      </Box>

      {/* Upload Area */}
      {!uploadedFile ? (
        <Paper
          {...getRootProps()}
          sx={{
            p: 4,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            textAlign: 'center',
            cursor: loading ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            opacity: loading ? 0.6 : 1,
            '&:hover': {
              borderColor: loading ? 'grey.300' : 'primary.main',
              backgroundColor: loading ? 'background.paper' : 'action.hover'
            }
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2, opacity: loading ? 0.5 : 1 }} />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Drop your document here' : 'Drag & drop your FRD document here'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            or click to browse files
          </Typography>
          <Button variant="contained" component="span" disabled={loading}>
            {loading ? 'Processing...' : 'Select Document'}
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, bgcolor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle sx={{ color: 'success.main', fontSize: 40 }} />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {uploadedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB • Ready for processing
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleRemoveFile} color="error" disabled={loading}>
              <Delete />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Features List */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          What will be generated:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><Description color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Technical Requirements Document (TRD)" 
              secondary="Comprehensive technical specifications, system requirements, and development guidelines"
            />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Our AI analyzes your functional requirements and generates a complete technical document ready for development teams.
        </Typography>
      </Paper>
    </Box>
  );
};

export default DocumentUpload;